import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import {ArrowLeft, ChartLine, Clock, Warning} from '@phosphor-icons/react';
import {SigninForm} from '../molecules/SigninForm';
import {NEUGENCE_API_BASE, bearerHeaders, fmtNumber, fmtTimestamp} from './_lib';

const PERIODS = [
	{key: 24, label: '24h'},
	{key: 168, label: '7d'},
	{key: 720, label: '30d'},
];

export default function PosthogSessions() {
	const navigate = useNavigate();
	const [periodH, setPeriodH] = useState(168);
	const [campaigns, setCampaigns] = useState([]);
	const [sessions, setSessions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [filterUtm, setFilterUtm] = useState(null);
	const signedIn = typeof window !== 'undefined' && !!sessionStorage.getItem('signedin');

	const load = useCallback(async () => {
		setLoading(true); setError(null);
		try {
			const headers = await bearerHeaders();
			const [c, s] = await Promise.all([
				axios.get(`${NEUGENCE_API_BASE}/api/v1/posthog/campaigns?period_h=${periodH}`, {headers}),
				axios.get(`${NEUGENCE_API_BASE}/api/v1/posthog/sessions?period_h=${periodH}${filterUtm?`&utm_campaign=${encodeURIComponent(filterUtm)}`:''}`, {headers}),
			]);
			setCampaigns((c.data && c.data.rows) || []);
			setSessions((s.data && s.data.rows) || []);
		} catch (e) { setError(e.message); }
		finally { setLoading(false); }
	}, [periodH, filterUtm]);

	useEffect(() => { if (signedIn) load(); }, [load, signedIn]);
	if (!signedIn) return <div style={{marginTop: 300}}><SigninForm/></div>;

	const totalSessions = campaigns.reduce((a,c) => a + (c.sessions||0), 0);
	const totalPeople = campaigns.reduce((a,c) => a + (c.people||0), 0);
	const totalPageviews = campaigns.reduce((a,c) => a + (c.pageviews||0), 0);

	return (
		<div className="min-h-screen bg-white text-left">
			<div className="max-w-6xl mx-auto px-6 py-10">
				<button onClick={() => navigate('/dashboard')} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6">
					<ArrowLeft size={14}/> Dashboard
				</button>
				<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
					<div>
						<h1 className="text-3xl font-semibold tracking-tight text-slate-900 flex items-center gap-3">
							<ChartLine size={28} weight="duotone" color="#8b5cf6"/> PostHog sessions
						</h1>
						<p className="text-sm text-slate-500 mt-1">Recipients who actually visited whipscribe.com after clicking a campaign CTA — with real session duration and pageview count. Joined by utm_campaign tag.</p>
					</div>
					<div role="tablist" className="inline-flex rounded-full bg-slate-100 p-1">
						{PERIODS.map(p => (
							<button key={p.key} onClick={() => setPeriodH(p.key)}
								className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${periodH===p.key?'bg-white text-slate-900 shadow-sm':'text-slate-600 hover:text-slate-900'}`}>{p.label}</button>
						))}
					</div>
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
					<div className="rounded-2xl border border-slate-200 bg-white p-5">
						<div className="text-xs text-slate-500 font-medium">Sessions</div>
						<div className="text-3xl font-semibold mt-1">{fmtNumber(totalSessions)}</div>
					</div>
					<div className="rounded-2xl border border-slate-200 bg-white p-5">
						<div className="text-xs text-slate-500 font-medium">People</div>
						<div className="text-3xl font-semibold mt-1">{fmtNumber(totalPeople)}</div>
					</div>
					<div className="rounded-2xl border border-slate-200 bg-white p-5">
						<div className="text-xs text-slate-500 font-medium">Pageviews</div>
						<div className="text-3xl font-semibold mt-1">{fmtNumber(totalPageviews)}</div>
					</div>
				</div>

				{error && <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 mb-6"><Warning size={18} className="text-amber-600 inline mr-2"/>{String(error)}</div>}
				{loading && <div className="text-center text-sm text-slate-500 py-10">Loading…</div>}

				{!loading && (
					<>
						<div className="rounded-2xl border border-slate-200 bg-white overflow-hidden mb-8">
							<div className="px-5 py-3 border-b border-slate-100">
								<span className="text-sm font-semibold text-slate-900">By campaign tag</span>
								<span className="ml-2 text-xs text-slate-500">click a row to filter the sessions table below</span>
							</div>
							<table className="w-full text-sm">
								<thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
									<tr>
										<th className="text-left px-5 py-3">utm_campaign</th>
										<th className="text-right px-3 py-3">Sessions</th>
										<th className="text-right px-3 py-3">People</th>
										<th className="text-right px-3 py-3">Pageviews</th>
										<th className="text-right px-3 py-3">Avg dwell</th>
									</tr>
								</thead>
								<tbody>
									{campaigns.length === 0 ? (
										<tr><td colSpan={5} className="px-5 py-12 text-center text-sm text-slate-500">No PostHog sessions in window. Recipients have to click the CTA for traffic to register.</td></tr>
									) : campaigns.map(c => (
										<tr key={c.utm_campaign} onClick={() => setFilterUtm(filterUtm===c.utm_campaign?null:c.utm_campaign)}
											className={`border-t border-slate-100 cursor-pointer hover:bg-violet-50/40 ${filterUtm===c.utm_campaign?'bg-violet-50':''}`}>
											<td className="px-5 py-2 text-slate-900 font-medium">{c.utm_campaign}</td>
											<td className="px-3 py-2 text-right text-slate-700">{c.sessions}</td>
											<td className="px-3 py-2 text-right text-slate-700">{c.people}</td>
											<td className="px-3 py-2 text-right text-slate-700">{c.pageviews}</td>
											<td className="px-3 py-2 text-right text-slate-700">{c.avg_dwell_s ? `${c.avg_dwell_s.toFixed(1)}s` : '—'}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						<div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
							<div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
								<Clock size={14}/>
								<span className="text-sm font-semibold text-slate-900">Top sessions by duration</span>
								{filterUtm && <span className="text-xs text-violet-700 ml-2">filtered: {filterUtm} <button className="text-slate-500 hover:text-slate-900 ml-1" onClick={() => setFilterUtm(null)}>✕</button></span>}
							</div>
							<table className="w-full text-sm">
								<thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
									<tr>
										<th className="text-right px-3 py-3">Duration</th>
										<th className="text-right px-3 py-3">Pageviews</th>
										<th className="text-left px-3 py-3">Country</th>
										<th className="text-left px-3 py-3">utm_campaign</th>
										<th className="text-left px-3 py-3">Distinct ID</th>
										<th className="text-left px-3 py-3">Started</th>
									</tr>
								</thead>
								<tbody>
									{sessions.length === 0 ? (
										<tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-slate-500">No sessions match.</td></tr>
									) : sessions.map((s,i) => (
										<tr key={s.session_id || i} className="border-t border-slate-100">
											<td className="px-3 py-2 text-right font-medium text-slate-900">{s.duration_s ? `${s.duration_s.toFixed(0)}s` : '0s'}</td>
											<td className="px-3 py-2 text-right text-slate-700">{s.pageviews}</td>
											<td className="px-3 py-2 text-slate-700">{s.country || '—'} {s.city ? `· ${s.city}` : ''}</td>
											<td className="px-3 py-2 text-slate-700 text-xs">{s.utm_campaign}</td>
											<td className="px-3 py-2 text-slate-500 text-xs font-mono">{(s.distinct_id||'').substring(0,12)}…</td>
											<td className="px-3 py-2 text-slate-500 text-xs">{fmtTimestamp(new Date(s.started_at).getTime())}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</>
				)}
			</div>
		</div>
	);
}
